import React, { useState, useEffect } from 'react';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box, Typography, Chip, Paper, Grid, Divider, Stack, Avatar, AvatarGroup, Tooltip,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupsIcon from '@mui/icons-material/Groups';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { calendarAPI } from '../api';

dayjs.extend(utc);
dayjs.extend(timezone);

const avatarUrl = (u) =>
  u && u.avatar && u.discordId
    ? `https://cdn.discordapp.com/avatars/${u.discordId}/${u.avatar}.png?size=64`
    : undefined;

const initial = (u) => (u && u.username ? u.username.charAt(0).toUpperCase() : '?');

// Custom calendar day: available days show their number in red, nothing else.
function AvailableDay({ availableSet = new Set(), day, ...other }) {
  const isAvail = availableSet.has(dayjs(day).format('YYYY-MM-DD'));
  return (
    <PickersDay
      {...other}
      day={day}
      sx={
        isAvail
          ? {
              color: '#D8352A !important',
              fontWeight: 700,
              backgroundColor: 'transparent !important',
              '&.Mui-selected': { color: '#D8352A !important', backgroundColor: 'transparent !important' },
              // No persistent highlight after click (keep the ripple splash though)
              '&.Mui-focusVisible, &:focus, &.Mui-selected.Mui-focusVisible, &.Mui-selected:focus': {
                backgroundColor: 'transparent !important',
              },
              '&:hover, &.Mui-selected:hover': {
                backgroundColor: 'rgba(255,255,255,0.08) !important',
              },
            }
          : undefined
      }
    />
  );
}

function Calendar() {
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [players, setPlayers] = useState([]);
  const [party, setParty] = useState([]);

  useEffect(() => {
    loadEvents();
    loadParty();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await calendarAPI.getEvents();
      const eventDates = new Set(
        response.data.filter(event => event.isAvailable).map(event => event.date)
      );
      setSelectedDates(eventDates);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const loadParty = async () => {
    try {
      const response = await calendarAPI.getAllAvailability();
      setPlayers(response.data.players || []);
      setParty(response.data.availability || []);
    } catch (error) {
      console.error('Failed to load party availability:', error);
    }
  };

  const formatDateForStorage = (date) => dayjs(date).format('YYYY-MM-DD');

  const handleDateChange = async (date) => {
    const dateStr = formatDateForStorage(date);
    try {
      if (selectedDates.has(dateStr)) {
        await calendarAPI.deleteEvent(dateStr);
        setSelectedDates(prev => {
          const newSet = new Set(prev);
          newSet.delete(dateStr);
          return newSet;
        });
      } else {
        await calendarAPI.saveEvent(dateStr, true);
        setSelectedDates(prev => new Set(prev).add(dateStr));
      }
      loadParty();
    } catch (error) {
      console.error('Failed to update event:', error);
    }
  };

  const sortedDates = Array.from(selectedDates).sort();
  const totalPlayers = players.length;
  const maxCount = party.reduce((m, d) => Math.max(m, d.users.length), 0);

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="overline" sx={{ color: 'secondary.main', fontSize: '0.72rem' }}>
          Session Planner
        </Typography>
        <Typography variant="h4" sx={{ color: 'text.primary', mt: 0.5, fontSize: { xs: '1.7rem', sm: '2.1rem' } }}>
          Your Availability
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1, maxWidth: 560 }}>
          Tap the days you're free to play.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Calendar panel */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 3, height: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                onChange={handleDateChange}
                shouldDisableDate={(date) => dayjs(date).isBefore(dayjs(), 'day')}
                dayOfWeekFormatter={(_day, weekday) => `${weekday.format('dd')}`}
                slots={{ day: AvailableDay }}
                slotProps={{ day: { availableSet: selectedDates } }}
                sx={{
                  width: '100%',
                  '& .MuiPickersCalendarHeader-label': {
                    fontFamily: '"Cinzel", serif',
                    fontWeight: 600,
                    fontSize: '1.05rem',
                  },
                  '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': { color: 'text.primary' },
                  '& .MuiPickersDay-root': {
                    fontSize: '0.9rem',
                    '&:hover': { backgroundColor: 'action.hover' },
                  },
                  // Kill MUI's built-in single-select filled circle; we color available days red ourselves.
                  '& .MuiPickersDay-root.Mui-selected': {
                    backgroundColor: 'transparent !important',
                  },
                  '& .MuiPickersDay-root.Mui-selected:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08) !important',
                  },
                  // Remove the persistent focus/selected circle left on the last-clicked day
                  '& .MuiPickersDay-root.Mui-focusVisible, & .MuiPickersDay-root:focus, & .MuiPickersDay-root.Mui-selected.Mui-focusVisible, & .MuiPickersDay-root.Mui-selected:focus': {
                    backgroundColor: 'transparent !important',
                  },
                  // Only the current day keeps a ring (neutral white).
                  '& .MuiPickersDay-today': {
                    border: '1px solid rgba(255,255,255,0.5)',
                    backgroundColor: 'transparent',
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  },
                }}
              />
            </LocalizationProvider>

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, pb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Red dates = you're available
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        {/* Your selected dates panel */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
              <EventAvailableIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ color: 'text.primary' }}>
                Your Dates
              </Typography>
              <Chip
                label={selectedDates.size}
                size="small"
                sx={{ ml: 'auto', fontWeight: 700, minWidth: 34, bgcolor: 'rgba(216,53,42,0.18)', color: '#F0A199', border: '1px solid rgba(216,53,42,0.4)' }}
              />
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {selectedDates.size === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                <EventAvailableIcon sx={{ fontSize: 42, color: 'text.secondary', opacity: 0.35, mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No dates selected yet. Tap dates on the calendar to add your availability.
                </Typography>
              </Box>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {sortedDates.map(date => (
                  <Chip
                    key={date}
                    label={dayjs(date).format('ddd, MMM D')}
                    variant="filled"
                    onDelete={() => handleDateChange(dayjs(date))}
                    sx={{
                      bgcolor: 'rgba(216,53,42,0.16)',
                      color: '#F0A199',
                      border: '1px solid rgba(216,53,42,0.34)',
                      '& .MuiChip-deleteIcon': { color: 'inherit', opacity: 0.7, '&:hover': { opacity: 1, color: '#F0A199' } },
                    }}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Party availability panel (full width) */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
              <GroupsIcon sx={{ color: 'secondary.main' }} />
              <Typography variant="h6" sx={{ color: 'text.primary' }}>
                Party Availability
              </Typography>
              {totalPlayers > 0 && (
                <Chip
                  label={`${totalPlayers} ${totalPlayers === 1 ? 'player' : 'players'}`}
                  size="small"
                  sx={{ ml: 'auto', bgcolor: 'rgba(201,164,92,0.16)', color: 'secondary.light', border: '1px solid rgba(201,164,92,0.34)', fontWeight: 600 }}
                />
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Upcoming nights the group is free — the gold nights work for the most players.
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {party.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <GroupsIcon sx={{ fontSize: 42, color: 'text.secondary', opacity: 0.35, mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No one has marked availability for upcoming dates yet.
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider flexItem />}>
                {party.map(({ date, users }) => {
                  const isTop = maxCount > 1 && users.length === maxCount;
                  const everyone = totalPlayers > 1 && users.length === totalPlayers;
                  return (
                    <Stack
                      key={date}
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1.5}
                      sx={{
                        py: 1.5,
                        px: 1.5,
                        mx: -1.5,
                        borderRadius: 2,
                        ...(isTop && {
                          background: 'linear-gradient(90deg, rgba(201,164,92,0.12), transparent 70%)',
                          boxShadow: 'inset 3px 0 0 0 #C9A45C',
                        }),
                      }}
                    >
                      <Box sx={{ minWidth: 130 }}>
                        <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>
                          {dayjs(date).format('ddd, MMM D')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {users.length} of {totalPlayers} free
                        </Typography>
                      </Box>

                      <AvatarGroup
                        max={6}
                        sx={{
                          '& .MuiAvatar-root': { width: 30, height: 30, fontSize: '0.8rem', bgcolor: 'primary.dark' },
                          justifyContent: 'flex-start',
                          flexDirection: 'row',
                        }}
                      >
                        {users.map((u) => (
                          <Tooltip key={u.id} title={u.username} arrow>
                            <Avatar src={avatarUrl(u)}>{initial(u)}</Avatar>
                          </Tooltip>
                        ))}
                      </AvatarGroup>

                      <Box sx={{ flexGrow: 1 }} />

                      {everyone ? (
                        <Chip label="Everyone free" size="small" color="secondary" sx={{ fontWeight: 700 }} />
                      ) : isTop ? (
                        <Chip
                          label="Top pick"
                          size="small"
                          sx={{ bgcolor: 'rgba(201,164,92,0.18)', color: 'secondary.light', border: '1px solid rgba(201,164,92,0.4)', fontWeight: 600 }}
                        />
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Calendar;
